import EventEmitter from 'eventemitter3';
import {
  AbstractWsConnector,
  JobOperationEventName,
  wsDefaultConnector,
  WsMessageOperationType,
  WsOperationMessageContent,
} from '@/ws';

export class JobOperationNotifier {
  protected readonly eventEmitter = new EventEmitter();

  protected readonly tempOperationsPool = new Map<string, WsOperationMessageContent>();

  constructor(protected readonly wsConnector: AbstractWsConnector) {
    this.wsConnector.on((type, content) => {
      if (type === WsMessageOperationType.OPERATION_FINISHED) {
        this.handleOperationFinished(content as WsOperationMessageContent);
      }
    });
  }

  protected handleOperationFinished(message: WsOperationMessageContent) {
    this.eventEmitter.emit(message.operationId, message);
    this.eventEmitter.emit(message.operationType, message);
    this.addOperationToPool(message);
  }

  protected addOperationToPool(message: WsOperationMessageContent) {
    this.tempOperationsPool.set(message.operationId, message);
    setTimeout(() => this.tempOperationsPool.delete(message.operationId), 10 * 1000);
  }

  protected executeOperationFromPool(operationId: string, handler: (message: WsOperationMessageContent) => void) {
    const message = this.tempOperationsPool.get(operationId);
    if (message) handler(message);
    return !!message;
  }

  once(eventName: JobOperationEventName, handler: (message: WsOperationMessageContent) => void) {
    if (this.executeOperationFromPool(eventName, handler)) return () => {};
    this.eventEmitter.once(eventName, handler);
    return () => this.off(eventName, handler, true);
  }

  on(eventName: JobOperationEventName, handler: (message: WsOperationMessageContent) => void) {
    this.executeOperationFromPool(eventName, handler);
    this.eventEmitter.on(eventName, handler);
    return () => this.off(eventName, handler);
  }

  off(eventName: JobOperationEventName, handler?: (message: WsOperationMessageContent) => void, once = false) {
    this.eventEmitter.off(eventName, handler, null, once);
  }
}

export const jobOperationNotifier = new JobOperationNotifier(wsDefaultConnector);
